import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Grid, makeStyles, Typography } from '@material-ui/core';
import { useRouteMatch } from 'react-router';

import { useProjectApi } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import { NatureVariation, LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';
import { ContentContainer, MapWrapper, TitleContainer } from 'src/components';
import { useFetchStatus } from 'src/hooks';
import { buildLeafletLayer } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { objectIsEmpty } from 'src/utils/util';
import { AspectCategoryStatusIcon } from 'src/scenes/Project/scenes/PressuresAndDependencies/components';
import { getConcernLevel, getHighestImpactCategory } from 'src/utils/aspectCategoriesUtils';


const mapOptions = {
  center: [ -32.48431324745671, -71.25296320202341 ],
  zoomControl: true,
  dragging: true,
  zoom: 16,
  doubleClickZoom: false,
  attributionControl: false,
};

const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  analyticCardPaper: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
}));

/*const impacts = [
  {
    componentLabel: 'Tierra',
    natureStateLabel: 'Proporción de vegetación natural',
    natureStateStatus: 'MA',
    pressureCategoryLabel: 'Cambio de uso de suelo',
    pressureCategoryStatus: 'MA',
    impactCategoryLabel: 'Degradación de la tierra',
    impactCategoryStatus: 'MA',
  },
  {
    componentLabel: 'Agua dulce',
    natureStateLabel: 'Disponibilidad de agua dulce',
    natureStateStatus: 'B',
    pressureCategoryLabel: 'Uso de agua dulce',
    pressureCategoryStatus: 'A',
    impactCategoryLabel: 'Escasez hídrica',
    impactCategoryStatus: 'M',
  },
  {
    componentLabel: 'Biodiversidad',
    natureStateLabel: 'Integridad de la biodiversidad',
    natureStateStatus: 'MA',
    pressureCategoryLabel: 'Introducción de especies',
    pressureCategoryStatus: 'MB',
    impactCategoryLabel: 'Perdida de la biodiversidad',
    impactCategoryStatus: 'M',
  },
];*/

const interestAreaMapHeight = 50;

const heightVH = n => document.documentElement.clientHeight * (n / 100);

const ProjectOverviewContainer = ({ interestArea, project }) => {

  const { isLoading: isLoadingInterestArea, isSuccess: isSuccessInterestArea } = interestArea.fetchStatus;
  const classes = useStyles();
  const match = useRouteMatch();
  const projectApi = useProjectApi();

  const { projectId } = match.params;

  const [ natureVariation, setNatureVariation ] = useState({});
  const [ impacts, setImpacts ] = useState([]);

  const { isLoading: isLoadingNatureVariation, isSuccess: isSuccessNatureVariation, handleConfirm: fetchNatureVariation } = useFetchStatus({
    confirmFn: async () => {
      const {
        freshwaterAvailability,
        ecosystemIntegrity,
        freshwaterQuality,
        nativeSpeciesAbundance,
        naturalVegetationCoverage,
      } = await projectApi.getNatureStateIndicators({ projectId });

      const years = [ 2020, 2021, 2022, 2023, 2024 ];
      const indicators = [
        { data: naturalVegetationCoverage, label: 'Proporción de vegetación natural' },
        { data: nativeSpeciesAbundance, label: 'Abundancia de especies nativas' },
        { data: ecosystemIntegrity, label: 'Integridad de los ecosistemas' },
        { data: freshwaterQuality, label: 'Calidad de agua dulce' },
        { data: freshwaterAvailability, label: 'Disponibilidad de agua dulce' },
      ];

      const lastestYear = years[years.length - 1];
      const lastestYearIndicatorValues = indicators.map(({ data }) => {
        const lastYearData = data.find(d => d.year === lastestYear);
        return lastYearData.value;
      });
      const impacts = lastestYearIndicatorValues.map(getConcernLevel);
      setImpacts(impacts);

      const datasets = years.reduce((acc, year) => {
        acc[year] = [];
        return acc;
      }, {});

      const processDataPoint = ({ year, value }) => {
        if (datasets[year]) {
          datasets[year].push(value ? value * 100 : 0);
        }
      };

      indicators.forEach(({ data }) => {
        if (Array.isArray(data)) {
          data.forEach(processDataPoint);
        }
      });

      const natureVariation = {
        labels: indicators.map(({ label }) => label),
        datasets: years.map(year => ({
          year,
          data: datasets[year],
        })),
        positiveImpactZone: 0,
        disabledLabels: [ 4, 1 ],
      };
      setNatureVariation(natureVariation);
    },
  });

  useEffect(() => {
    fetchNatureVariation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasNatureVariation = !objectIsEmpty(natureVariation);
  const hasInterestArea = interestArea.data.length > 0;

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        project={project}
        breadcrumbs={[ 'projects', 'project', { name: 'Visión general' } ]}>
        Estado de la naturaleza - Visión general
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        {(!hasInterestArea && isSuccessInterestArea) &&
          <Grid container spacing={1} item>
            <Grid item xs={12}>
              <Box my={2}>
                <Typography variant="h6" color="textSecondary" align="center">
                  No se ha definido un área de interés para este proyecto.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Para visualizar las métricas del proyecto, primero debe definir un área de interés.
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center">
                  Puede hacerlo desde la sección de <strong>Áreas de interés</strong> en el menú del proyecto.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        }
        {hasInterestArea &&
          <>
            <Grid container spacing={1} item>
              <Grid item xs={6}>
                {/* <Box my={1} /> */}
                <AnalyticCard title="Área de interés" paperProps={{ className: classes.analyticCardPaper }}>
                  <Divider />
                  <Box mt={0.5} />
                  {isLoadingInterestArea && <LoadingBox label="área de interes" />}
                  {isSuccessInterestArea &&
                    <MapWrapper
                      id="interest-area-map"
                      className={classes.map}
                      options={mapOptions}
                      height={isLoadingInterestArea ? 0 : heightVH(interestAreaMapHeight)}
                      layers={
                        interestArea.data.map(amd => buildLeafletLayer({
                          features: amd.geojson,
                          id: amd.id,
                          name: amd.name,
                        }))
                      }
                      fitBoundsOnUpdate
                    />
                  }
                </AnalyticCard>
              </Grid>
              <Grid item xs={6}>
                <AnalyticCard title="Estado de la naturaleza" >
                  <Divider />
                  <Box mt={2} />
                  {isLoadingNatureVariation && <LoadingBox label="estado de la naturaleza" />}
                  {!isLoadingNatureVariation && (() => {
                    const highest = getHighestImpactCategory(impacts);
                    return (
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                          Nivel de preocupación del proyecto (considerando el nivel de preocupación más alto)
                        </Typography>
                        <Box ml={1}>
                          <AspectCategoryStatusIcon category={highest} size="big" />
                        </Box>
                      </Box>
                    );
                  })()}
                </AnalyticCard>
                <Box my={1} />
                <AnalyticCard title="Estado de la naturaleza" >
                  <Divider />
                  <Box mt={1}>
                    {(isLoadingNatureVariation || isLoadingInterestArea) && <LoadingBox label="variación en la naturaleza" />}
                    {(hasNatureVariation && isSuccessNatureVariation && isSuccessInterestArea) &&
                      <NatureVariation natureVariation={natureVariation} />
                    }
                  </Box>
                </AnalyticCard>
              </Grid>
            </Grid>
          </>
        }
      </ContentContainer>
    </Grid>
  );
};

ProjectOverviewContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array,
    fetchStatus: PropTypes.object,
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { ProjectOverviewContainer };